import "./style/Coaches.css";
import "./style/CreditsDashboard.css";
import { useEffect, useMemo, useState } from "react";
import debounce from "lodash.debounce";
import { isAuthenticated } from "../apicalls/auth";
import { getCreditLedger, manualConsumeCredit } from "../apicalls/credits";
import {
  confirmHeldLessonCredit,
  getPackageCreditsBalance,
  getPackagePurchases,
  previewLessonCreditForPlayer,
  rollbackLessonCreditForParticipant,
  useLessonCreditForPlayer as applyLessonCreditForPlayer,
} from "../apicalls/packages";
import { getAllPlayers } from "../apicalls/players";
import Paginations from "./Paginations";
import {
  filterManualPurchaseOptions,
  formatManualPurchaseOption,
  formatPurchaseDate,
  getPurchaseAvailableCredits,
} from "./creditsDashboardUtils";

const entryTypes = ["EARN", "HOLD", "RELEASE", "CONSUME", "EXPIRE", "REFUND"];

const normalizeLedgerRows = (data) => {
  if (Array.isArray(data?.entries)) {
    return data.entries;
  }
  if (Array.isArray(data?.data)) {
    return data.data;
  }
  if (Array.isArray(data?.results)) {
    return data.results;
  }
  if (Array.isArray(data)) {
    return data;
  }
  return [];
};

const normalizePurchasesRows = (data) => {
  if (Array.isArray(data?.purchases)) {
    return data.purchases;
  }
  if (Array.isArray(data?.data)) {
    return data.data;
  }
  if (Array.isArray(data?.results)) {
    return data.results;
  }
  if (Array.isArray(data)) {
    return data;
  }
  return [];
};

const normalizePlayerRows = (data) => {
  if (Array.isArray(data?.players)) {
    return data.players;
  }
  if (Array.isArray(data?.data)) {
    return data.data;
  }
  if (Array.isArray(data?.results)) {
    return data.results;
  }
  if (Array.isArray(data)) {
    return data;
  }
  return [];
};

const normalizeBalanceSummary = (data) => {
  if (!data || Array.isArray(data)) {
    return null;
  }
  const summaryKeys = [
    "available",
    "held",
    "total",
    "earned",
    "consumed",
    "expired",
    "refunded",
  ];
  const summary = {};
  let hasKey = false;
  summaryKeys.forEach((key) => {
    if (data[key] !== undefined) {
      summary[key] = Number(data[key]) || 0;
      hasKey = true;
    }
  });
  if (!hasKey && data?.totals) {
    summaryKeys.forEach((key) => {
      if (data.totals[key] !== undefined) {
        summary[key] = Number(data.totals[key]) || 0;
        hasKey = true;
      }
    });
  }
  if (data?.breakdown) {
    summaryKeys.forEach((key) => {
      if (data.breakdown[key] !== undefined) {
        summary[key] = Number(data.breakdown[key]) || 0;
        hasKey = true;
      }
    });
  }
  if (!hasKey) {
    return null;
  }
  return {
    ...summary,
    by_coach: Array.isArray(data.by_coach) ? data.by_coach : [],
    by_player: Array.isArray(data.by_player) ? data.by_player : [],
    by_package: Array.isArray(data.by_package) ? data.by_package : [],
  };
};

const formatNumber = (value) => {
  if (value === null || value === undefined) {
    return "-";
  }
  const num = Number(value);
  if (Number.isNaN(num)) {
    return String(value);
  }
  return num.toLocaleString();
};

const buildManualConsumeIdempotencyKey = () =>
  `manual-consume:${Date.now()}:${Math.random().toString(16).slice(2)}`;

const formatCreditError = (error, fallback) => {
  if (
    error?.code === "package_charge_failed" ||
    error?.data?.code === "package_charge_failed" ||
    error?.requires_action
  ) {
    const intentText = error.payment_intent_id
      ? ` PaymentIntent: ${error.payment_intent_id}.`
      : "";
    return `Package charge failed. Lesson was not confirmed. Player must update card or complete 3DS, then retry.${intentText}`;
  }
  return error?.message || fallback;
};

const getPurchasePaymentState = (purchase) => {
  if (!purchase) {
    return {
      label: "Unknown",
      className: "package-status-badge package-status-unknown",
      isReserved: false,
    };
  }
  if (purchase.status === "reserved" || purchase.paid === false) {
    return {
      label: "Reserved - charge on confirm",
      className: "package-status-badge package-status-reserved",
      isReserved: true,
    };
  }
  if (
    purchase.paid === true ||
    purchase.status === "active" ||
    purchase.status === "succeeded"
  ) {
    return {
      label: "Paid",
      className: "package-status-badge package-status-paid",
      isReserved: false,
    };
  }
  return {
    label: purchase.status || "Unknown",
    className: "package-status-badge package-status-unknown",
    isReserved: false,
  };
};

const buildPackageMap = (purchases) => {
  const map = new Map();
  purchases.forEach((purchase) => {
    const keyCandidates = [
      purchase.coach_package_id,
      purchase.package_id,
      purchase.id,
      purchase.ref_id,
    ];
    const key = keyCandidates.find((candidate) => candidate !== undefined);
    if (key !== undefined) {
      map.set(String(key), purchase);
    }
  });
  return map;
};

const buildPurchaseMap = (purchases) => {
  const map = new Map();
  purchases.forEach((purchase) => {
    if (purchase.id !== undefined && purchase.id !== null) {
      map.set(String(purchase.id), purchase);
    }
  });
  return map;
};

const getPackageLabel = (packageMap, entry) => {
  const keyCandidates = [
    entry.coach_package_id,
    entry.ref_id,
    entry.package_id,
  ];
  const key = keyCandidates.find((candidate) => candidate !== undefined);
  if (key === undefined) {
    return "-";
  }
  const purchase = packageMap.get(String(key));
  return (
    purchase?.package_name ||
    purchase?.package_title ||
    purchase?.package?.name ||
    `Package ${key}`
  );
};

const accumulateEntryTotals = (totals, entry) => {
  const amount = Number(entry.amount) || 0;
  switch (entry.entry_type) {
    case "EARN":
      totals.earned += amount;
      break;
    case "CONSUME":
      totals.consumed += amount;
      break;
    case "EXPIRE":
      totals.expired += amount;
      break;
    case "REFUND":
      totals.refunded += amount;
      break;
    case "HOLD":
      totals.held += amount;
      break;
    case "RELEASE":
      totals.released += amount;
      break;
    default:
      break;
  }
};

const finalizeTotals = (totals) => {
  const held = totals.held - totals.released;
  const earned = totals.earned;
  const consumed = totals.consumed;
  const expired = totals.expired;
  const refunded = totals.refunded;
  const total = earned - consumed - expired - refunded;
  const available = total - held;
  return {
    available,
    held,
    total,
    earned,
    consumed,
    expired,
    refunded,
  };
};

const createAggregateMap = (entries, keyGetter, infoGetter) => {
  const map = new Map();
  entries.forEach((entry) => {
    const key = keyGetter(entry);
    if (!key) {
      return;
    }
    if (!map.has(key)) {
      map.set(key, {
        key,
        ...infoGetter(entry),
        earned: 0,
        consumed: 0,
        expired: 0,
        refunded: 0,
        held: 0,
        released: 0,
      });
    }
    accumulateEntryTotals(map.get(key), entry);
  });
  return Array.from(map.values()).map((row) => ({
    ...row,
    ...finalizeTotals(row),
  }));
};

const getBalanceBreakdownValue = (row, key) => {
  const value = row?.breakdown?.[key] ?? row?.[key];
  return Number(value) || 0;
};

const normalizeBalanceCoachRows = (rows = []) =>
  rows.map((row) => ({
    key: String(row.coach_id),
    name: row.coach_full_name || "Unknown coach",
    email: row.coach_email || "-",
    coach_id: row.coach_id,
    available: Number(row.available) || 0,
    held: Number(row.held) || 0,
    total: Number(row.total) || 0,
    earned: getBalanceBreakdownValue(row, "earned"),
    consumed: getBalanceBreakdownValue(row, "consumed"),
    expired: getBalanceBreakdownValue(row, "expired"),
    refunded: getBalanceBreakdownValue(row, "refunded"),
  }));

const normalizeBalancePlayerRows = (rows = []) =>
  rows.map((row) => ({
    key: String(row.player_id),
    name: row.player_full_name || "Unknown player",
    email: row.player_email || "-",
    user_id: row.player_id,
    available: Number(row.available) || 0,
    held: Number(row.held) || 0,
    total: Number(row.total) || 0,
    earned: getBalanceBreakdownValue(row, "earned"),
    consumed: getBalanceBreakdownValue(row, "consumed"),
    expired: getBalanceBreakdownValue(row, "expired"),
    refunded: getBalanceBreakdownValue(row, "refunded"),
  }));

const normalizeBalancePackageRows = (rows = [], purchaseMap = new Map()) =>
  rows.map((row) => {
    const purchase = purchaseMap.get(String(row.purchase_id));
    return {
      key: String(row.purchase_id),
      name: row.package_name || purchase?.package_name || `Package ${row.purchase_id}`,
      email: purchase?.coach_email || row.coach_email || "-",
      coach_name: purchase?.coach_full_name || row.coach_full_name || `Coach ${row.coach_id || "-"}`,
      coach_id: row.coach_id,
      package_id: row.coach_package_id || row.purchase_id,
      available: Number(row.available) || 0,
      held: Number(row.held) || 0,
      total: Number(row.total) || 0,
      earned: getBalanceBreakdownValue(row, "earned"),
      consumed: getBalanceBreakdownValue(row, "consumed"),
      expired: getBalanceBreakdownValue(row, "expired"),
      refunded: getBalanceBreakdownValue(row, "refunded"),
    };
  });

const getEntryMeta = (entry) => {
  if (!entry?.meta) {
    return {};
  }
  if (typeof entry.meta === "string") {
    try {
      return JSON.parse(entry.meta) || {};
    } catch (error) {
      return {};
    }
  }
  return entry.meta;
};

const getCreditUsageKey = (entry) => {
  const meta = getEntryMeta(entry);
  return [
    entry.user_id,
    entry.ref_id,
    meta.purchase_id,
    meta.participant_id ?? "none",
  ].join(":");
};

const buildHeldLessonRows = (entries) => {
  const map = new Map();

  entries.forEach((entry) => {
    if (entry.ref_type !== "lesson") {
      return;
    }
    if (!["HOLD", "RELEASE", "CONSUME"].includes(entry.entry_type)) {
      return;
    }

    const meta = getEntryMeta(entry);
    const key = getCreditUsageKey(entry);
    if (!map.has(key)) {
      map.set(key, {
        key,
        player_id: entry.user_id,
        player_name: entry.player_full_name || "Unknown player",
        player_email: entry.player_email || "-",
        coach_id: entry.coach_id,
        coach_name: entry.coach_full_name || "Unknown coach",
        coach_email: entry.coach_email || "-",
        lesson_id: entry.ref_id,
        participant_id: meta.participant_id ?? null,
        purchase_id: meta.purchase_id ?? null,
        lesson_type: meta.lesson_type || "-",
        held: 0,
        released: 0,
        consumed: 0,
        created_at: entry.created_at,
      });
    }

    const row = map.get(key);
    const amount = Number(entry.amount) || 0;
    if (entry.entry_type === "HOLD") {
      row.held += amount;
      if (!row.created_at || new Date(entry.created_at) > new Date(row.created_at)) {
        row.created_at = entry.created_at;
      }
    } else if (entry.entry_type === "RELEASE") {
      row.released += amount;
    } else if (entry.entry_type === "CONSUME") {
      row.consumed += amount;
    }
  });

  return Array.from(map.values())
    .map((row) => ({
      ...row,
      active_held: Math.max(row.held - row.released - row.consumed, 0),
    }))
    .filter((row) => row.active_held > 0)
    .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
};

const paginateRows = (rows, page, perPage) => {
  const start = (page - 1) * perPage;
  return rows.slice(start, start + perPage);
};

function CreditsDashboard() {
  const { access_token } = isAuthenticated();

  const [ledgerEntries, setLedgerEntries] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [balanceSummary, setBalanceSummary] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const [loadingLedger, setLoadingLedger] = useState(true);
  const [loadingPurchases, setLoadingPurchases] = useState(true);
  const [loadingBalances, setLoadingBalances] = useState(true);
  const [confirmingHeldKey, setConfirmingHeldKey] = useState("");
  const [confirmHeldError, setConfirmHeldError] = useState("");
  const [confirmHeldMessage, setConfirmHeldMessage] = useState("");
  const [useCreditLessonId, setUseCreditLessonId] = useState("");
  const [useCreditPlayerSearch, setUseCreditPlayerSearch] = useState("");
  const [useCreditPlayerId, setUseCreditPlayerId] = useState("");
  const [useCreditPlayers, setUseCreditPlayers] = useState([]);
  const [useCreditPreview, setUseCreditPreview] = useState(null);
  const [useCreditPurchaseId, setUseCreditPurchaseId] = useState("");
  const [useCreditLoading, setUseCreditLoading] = useState(false);
  const [useCreditPreviewLoading, setUseCreditPreviewLoading] = useState(false);
  const [useCreditError, setUseCreditError] = useState("");
  const [useCreditMessage, setUseCreditMessage] = useState("");
  const [rollbackLessonId, setRollbackLessonId] = useState("");
  const [rollbackParticipantId, setRollbackParticipantId] = useState("");
  const [rollbackLoading, setRollbackLoading] = useState(false);
  const [rollbackError, setRollbackError] = useState("");
  const [rollbackMessage, setRollbackMessage] = useState("");
  const [manualPurchaseId, setManualPurchaseId] = useState("");
  const [manualPurchaseSearch, setManualPurchaseSearch] = useState("");
  const [manualAmount, setManualAmount] = useState("");
  const [manualReason, setManualReason] = useState("");
  const [manualLoading, setManualLoading] = useState(false);
  const [manualError, setManualError] = useState("");
  const [manualMessage, setManualMessage] = useState("");

  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [coachFilter, setCoachFilter] = useState("");
  const [playerFilter, setPlayerFilter] = useState("");
  const [packageFilter, setPackageFilter] = useState("");
  const [entryTypeFilter, setEntryTypeFilter] = useState("");
  const [search, setSearch] = useState("");

  const [activeTab, setActiveTab] = useState("coach");
  const [perPage, setPerPage] = useState(10);
  const [pageCoach, setPageCoach] = useState(1);
  const [pagePlayer, setPagePlayer] = useState(1);
  const [pagePackage, setPagePackage] = useState(1);
  const [pageHeld, setPageHeld] = useState(1);

  useEffect(() => {
    let isMounted = true;
    setLoadingLedger(true);
    getCreditLedger(access_token, { perPage: 250, page: 1 }).then((data) => {
      if (!isMounted) {
        return;
      }
      setLedgerEntries(normalizeLedgerRows(data));
      setLoadingLedger(false);
    });
    return () => {
      isMounted = false;
    };
  }, [access_token, refreshKey]);

  useEffect(() => {
    let isMounted = true;
    setLoadingPurchases(true);
    getPackagePurchases(access_token, { perPage: 500, page: 1 }).then(
      (data) => {
        if (!isMounted) {
          return;
        }
        setPurchases(normalizePurchasesRows(data));
        setLoadingPurchases(false);
      }
    );
    return () => {
      isMounted = false;
    };
  }, [access_token, refreshKey]);

  useEffect(() => {
    let isMounted = true;
    setLoadingBalances(true);
    getPackageCreditsBalance(access_token).then((data) => {
      if (!isMounted) {
        return;
      }
      setBalanceSummary(normalizeBalanceSummary(data));
      setLoadingBalances(false);
    });
    return () => {
      isMounted = false;
    };
  }, [access_token, refreshKey]);

  useEffect(() => {
    if (!access_token) {
      return undefined;
    }
    const timeout = setTimeout(() => {
      getAllPlayers(1, 10, useCreditPlayerSearch, access_token).then((data) => {
        setUseCreditPlayers(normalizePlayerRows(data));
      });
    }, 300);
    return () => clearTimeout(timeout);
  }, [access_token, useCreditPlayerSearch]);

  useEffect(() => {
    setUseCreditPreview(null);
    setUseCreditPurchaseId("");
    if (!access_token || !useCreditLessonId || !useCreditPlayerId) {
      return undefined;
    }

    let isMounted = true;
    setUseCreditPreviewLoading(true);
    setUseCreditError("");
    previewLessonCreditForPlayer(
      access_token,
      useCreditLessonId,
      useCreditPlayerId
    )
      .then((data) => {
        if (!isMounted) {
          return;
        }
        setUseCreditPreview(data);
        const firstPurchase = data?.compatible_purchases?.[0];
        if (firstPurchase?.purchase_id) {
          setUseCreditPurchaseId(String(firstPurchase.purchase_id));
        }
      })
      .catch((error) => {
        if (!isMounted) {
          return;
        }
        setUseCreditError(error.message || "Failed to load lesson credit preview.");
      })
      .finally(() => {
        if (isMounted) {
          setUseCreditPreviewLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [access_token, useCreditLessonId, useCreditPlayerId, refreshKey]);

  const debouncedSearch = useMemo(
    () =>
      debounce((value) => {
        setSearch(value);
      }, 300),
    []
  );

  useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

  useEffect(() => {
    setPageCoach(1);
    setPagePlayer(1);
    setPagePackage(1);
    setPageHeld(1);
  }, [
    dateFrom,
    dateTo,
    coachFilter,
    playerFilter,
    packageFilter,
    entryTypeFilter,
    search,
    perPage,
  ]);

  const packageMap = useMemo(
    () => buildPackageMap(purchases),
    [purchases]
  );
  const purchaseMap = useMemo(
    () => buildPurchaseMap(purchases),
    [purchases]
  );

  const filteredLedger = useMemo(() => {
    const coachQuery = coachFilter.trim().toLowerCase();
    const playerQuery = playerFilter.trim().toLowerCase();
    const packageQuery = packageFilter.trim().toLowerCase();
    const searchQuery = search.trim().toLowerCase();

    const fromDate = dateFrom ? new Date(dateFrom) : null;
    const toDate = dateTo ? new Date(dateTo) : null;
    if (toDate) {
      toDate.setHours(23, 59, 59, 999);
    }

    return ledgerEntries.filter((entry) => {
      const createdAt = entry.created_at || entry.updated_at;
      if (fromDate && createdAt) {
        const dateValue = new Date(createdAt);
        if (dateValue < fromDate) {
          return false;
        }
      }
      if (toDate && createdAt) {
        const dateValue = new Date(createdAt);
        if (dateValue > toDate) {
          return false;
        }
      }

      if (entryTypeFilter && entry.entry_type !== entryTypeFilter) {
        return false;
      }

      const coachText = `${entry.coach_full_name || ""} ${
        entry.coach_email || ""
      } ${entry.coach_id || ""}`.toLowerCase();
      if (coachQuery && !coachText.includes(coachQuery)) {
        return false;
      }

      const playerText = `${entry.player_full_name || ""} ${
        entry.player_email || ""
      } ${entry.user_id || ""}`.toLowerCase();
      if (playerQuery && !playerText.includes(playerQuery)) {
        return false;
      }

      if (packageQuery) {
        const packageLabel = getPackageLabel(packageMap, entry).toLowerCase();
        const packageText = `${packageLabel} ${
          entry.coach_package_id || ""
        } ${entry.ref_id || ""}`.toLowerCase();
        if (!packageText.includes(packageQuery)) {
          return false;
        }
      }

      if (searchQuery) {
        const searchText = [
          entry.player_full_name,
          entry.player_email,
          entry.coach_full_name,
          entry.coach_email,
          entry.entry_type,
          entry.ref_type,
          entry.idempotency_key,
          entry.ref_id,
          entry.coach_package_id,
          getPackageLabel(packageMap, entry),
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        if (!searchText.includes(searchQuery)) {
          return false;
        }
      }

      return true;
    });
  }, [
    ledgerEntries,
    coachFilter,
    playerFilter,
    packageFilter,
    entryTypeFilter,
    search,
    dateFrom,
    dateTo,
    packageMap,
  ]);

  const derivedSummary = useMemo(() => {
    const totals = {
      earned: 0,
      consumed: 0,
      expired: 0,
      refunded: 0,
      held: 0,
      released: 0,
    };
    filteredLedger.forEach((entry) => {
      accumulateEntryTotals(totals, entry);
    });
    return finalizeTotals(totals);
  }, [filteredLedger]);

  const hasFilters =
    dateFrom ||
    dateTo ||
    coachFilter ||
    playerFilter ||
    packageFilter ||
    entryTypeFilter ||
    search;
  const summary = hasFilters ? derivedSummary : balanceSummary || derivedSummary;

  const byCoach = useMemo(() => {
    if (!hasFilters && balanceSummary?.by_coach?.length) {
      return normalizeBalanceCoachRows(balanceSummary.by_coach);
    }
    return createAggregateMap(
      filteredLedger,
      (entry) => entry.coach_id,
      (entry) => ({
        name: entry.coach_full_name || "Unknown coach",
        email: entry.coach_email || "-",
        coach_id: entry.coach_id,
      })
    );
  }, [balanceSummary, filteredLedger, hasFilters]);

  const byPlayer = useMemo(() => {
    if (!hasFilters && balanceSummary?.by_player?.length) {
      return normalizeBalancePlayerRows(balanceSummary.by_player);
    }
    return createAggregateMap(
      filteredLedger,
      (entry) => entry.user_id,
      (entry) => ({
        name: entry.player_full_name || "Unknown player",
        email: entry.player_email || "-",
        user_id: entry.user_id,
      })
    );
  }, [balanceSummary, filteredLedger, hasFilters]);

  const byPackage = useMemo(() => {
    if (!hasFilters && balanceSummary?.by_package?.length) {
      return normalizeBalancePackageRows(balanceSummary.by_package, purchaseMap);
    }
    return createAggregateMap(
      filteredLedger,
      (entry) => entry.coach_package_id || entry.ref_id,
      (entry) => ({
        name: getPackageLabel(packageMap, entry),
        email: entry.coach_email || "-",
        coach_name: entry.coach_full_name || "Unknown coach",
        coach_id: entry.coach_id,
        package_id: entry.coach_package_id || entry.ref_id,
      })
    );
  }, [balanceSummary, filteredLedger, hasFilters, packageMap, purchaseMap]);

  const coachRows = paginateRows(byCoach, pageCoach, perPage);
  const playerRows = paginateRows(byPlayer, pagePlayer, perPage);
  const packageRows = paginateRows(byPackage, pagePackage, perPage);
  const heldLessonRows = useMemo(
    () => buildHeldLessonRows(filteredLedger),
    [filteredLedger]
  );
  const heldRows = paginateRows(heldLessonRows, pageHeld, perPage);
  const selectedUseCreditPlayer = useMemo(
    () =>
      useCreditPlayers.find(
        (player) => String(player.id || player.user_id) === String(useCreditPlayerId)
      ),
    [useCreditPlayers, useCreditPlayerId]
  );
  const previewPurchases = useCreditPreview?.compatible_purchases || [];
  const selectedPreviewPurchase = previewPurchases.find(
    (purchase) => String(purchase.purchase_id) === String(useCreditPurchaseId)
  );
  const manualPurchase = purchases.find(
    (purchase) => String(purchase.id) === String(manualPurchaseId)
  );
  const manualPurchaseOptions = useMemo(
    () => filterManualPurchaseOptions(purchases, manualPurchaseSearch, manualPurchaseId),
    [purchases, manualPurchaseSearch, manualPurchaseId]
  );
  const manualAvailable = getPurchaseAvailableCredits(manualPurchase);
  const manualAmountNumber = Number(manualAmount);
  const manualSubmitDisabled =
    manualLoading ||
    !manualPurchase ||
    !Number.isInteger(manualAmountNumber) ||
    manualAmountNumber < 1 ||
    !manualReason.trim() ||
    manualAmountNumber > manualAvailable;

  const isLoading =
    loadingLedger || loadingPurchases || loadingBalances || false;

  const handleConfirmHeldCredit = async (row) => {
    if (!row.lesson_id || !row.participant_id) {
      setConfirmHeldError("Missing lesson or participant ID for this hold.");
      return;
    }
    const confirmed = window.confirm(
      row.payment_state?.isReserved
        ? `Confirm held credit for ${row.player_name} on lesson ${row.lesson_id}? This will charge the full reserved package.`
        : `Confirm held credit for ${row.player_name} on lesson ${row.lesson_id}?`
    );
    if (!confirmed) {
      return;
    }

    setConfirmingHeldKey(row.key);
    setConfirmHeldError("");
    setConfirmHeldMessage("");
    try {
      await confirmHeldLessonCredit(access_token, row.lesson_id, row.participant_id);
      setConfirmHeldMessage(
        `Confirmed lesson ${row.lesson_id} for ${row.player_name}.`
      );
      setRefreshKey((current) => current + 1);
    } catch (error) {
      setConfirmHeldError(formatCreditError(error, "Failed to confirm held credit."));
    } finally {
      setConfirmingHeldKey("");
    }
  };

  const handleUseCreditForLesson = async (event) => {
    event.preventDefault();
    if (!useCreditLessonId || !useCreditPlayerId) {
      setUseCreditError("Lesson ID and player are required.");
      return;
    }
    if (!previewPurchases.length) {
      setUseCreditError("This player has no compatible available credits for this lesson.");
      return;
    }

    const confirmed = window.confirm(
      `Use credit for ${selectedUseCreditPlayer?.full_name || `player ${useCreditPlayerId}`} on lesson ${useCreditLessonId}?`
    );
    if (!confirmed) {
      return;
    }

    setUseCreditLoading(true);
    setUseCreditError("");
    setUseCreditMessage("");
    try {
      const result = await applyLessonCreditForPlayer(
        access_token,
        useCreditLessonId,
        useCreditPlayerId,
        useCreditPurchaseId
      );
      setUseCreditMessage(
        `Credit used and lesson confirmed for player ${result.player_id}. Participant ${result.participant_id}.`
      );
      setUseCreditLessonId("");
      setUseCreditPlayerId("");
      setUseCreditPlayerSearch("");
      setUseCreditPreview(null);
      setUseCreditPurchaseId("");
      setRefreshKey((current) => current + 1);
    } catch (error) {
      setUseCreditError(formatCreditError(error, "Failed to use credit for lesson."));
    } finally {
      setUseCreditLoading(false);
    }
  };

  const handleRollbackCreditForLesson = async (event) => {
    event.preventDefault();
    if (!rollbackLessonId || !rollbackParticipantId) {
      setRollbackError("Lesson ID and participant ID are required.");
      return;
    }

    const confirmed = window.confirm(
      `Rollback credit for lesson ${rollbackLessonId}, participant ${rollbackParticipantId}?`
    );
    if (!confirmed) {
      return;
    }

    setRollbackLoading(true);
    setRollbackError("");
    setRollbackMessage("");
    try {
      const result = await rollbackLessonCreditForParticipant(
        access_token,
        rollbackLessonId,
        rollbackParticipantId
      );
      setRollbackMessage(
        `Credit rolled back for participant ${result.participant_id}.`
      );
      setRollbackLessonId("");
      setRollbackParticipantId("");
      setRefreshKey((current) => current + 1);
    } catch (error) {
      setRollbackError(error.message || "Failed to rollback credit.");
    } finally {
      setRollbackLoading(false);
    }
  };

  const handleManualConsumeCredit = async (event) => {
    event.preventDefault();
    if (!manualPurchase) {
      setManualError("Select a package purchase.");
      return;
    }
    if (!Number.isInteger(manualAmountNumber) || manualAmountNumber < 1) {
      setManualError("Enter a positive whole-credit amount.");
      return;
    }
    if (!manualReason.trim()) {
      setManualError("Reason is required.");
      return;
    }
    if (manualAmountNumber > manualAvailable) {
      setManualError(`Amount cannot exceed ${manualAvailable} available credits.`);
      return;
    }

    const confirmed = window.confirm(
      `Consume ${manualAmountNumber} credits from purchase ${manualPurchase.id}? This writes a permanent CONSUME ledger entry.`
    );
    if (!confirmed) {
      return;
    }

    setManualLoading(true);
    setManualError("");
    setManualMessage("");
    try {
      const result = await manualConsumeCredit(access_token, {
        purchase_id: manualPurchase.id,
        amount: manualAmountNumber,
        reason: manualReason.trim(),
        idempotency_key: buildManualConsumeIdempotencyKey(),
      });
      setManualMessage(
        `Consumed ${manualAmountNumber} credits from purchase ${manualPurchase.id}. ${result.balance?.available ?? 0} available remain.`
      );
      setManualPurchaseId("");
      setManualPurchaseSearch("");
      setManualAmount("");
      setManualReason("");
      setRefreshKey((current) => current + 1);
    } catch (error) {
      setManualError(error.message || "Failed to adjust credit balance.");
    } finally {
      setManualLoading(false);
    }
  };

  return (
    <main className="credits-dashboard">
      <div className="coaches_title">
        <h1>Credits Dashboard</h1>
      </div>

      <div className="row g-3 mb-3">
        {[
          { label: "Available", value: summary.available },
          { label: "Held", value: summary.held },
          { label: "Total", value: summary.total },
          { label: "Earned", value: summary.earned },
          { label: "Consumed", value: summary.consumed },
          { label: "Expired", value: summary.expired },
          { label: "Refunded", value: summary.refunded },
        ].map((card) => (
          <div className="col-6 col-md-3 col-lg-2" key={card.label}>
            <div className="card credits-summary-card h-100">
              <div className="card-body">
                <p className="card-title credits-summary-title">
                  {card.label}
                </p>
                <h5 className="credits-summary-value">
                  {formatNumber(card.value)}
                </h5>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="alert alert-info py-2">
        Reserved package credits are unpaid until first lesson confirmation. If the
        deferred Stripe charge fails, the lesson stays pending and the player must fix
        payment before retry.
      </div>

      <div className="card mb-3 credits-use-card">
        <div className="card-body">
          <div className="row g-4">
            <div className="col-12 col-lg-7">
              <div className="mb-3">
                <h5 className="mb-1">Use credit for lesson</h5>
                <p className="text-muted mb-0">
                  Select a player, load lesson details, then apply one compatible purchased credit.
                </p>
              </div>
              {useCreditError ? (
                <div className="alert alert-danger py-2">{useCreditError}</div>
              ) : null}
              {useCreditMessage ? (
                <div className="alert alert-success py-2">{useCreditMessage}</div>
              ) : null}
              <form className="row g-2 align-items-end" onSubmit={handleUseCreditForLesson}>
                <div className="col-12 col-md-3">
                  <label className="form-label">Lesson ID</label>
                  <input
                    type="number"
                    className="form-control"
                    value={useCreditLessonId}
                    onChange={(event) => setUseCreditLessonId(event.target.value)}
                    placeholder="2038"
                    min="1"
                  />
                </div>
                <div className="col-12 col-md-5">
                  <label className="form-label">Player</label>
                  <input
                    type="search"
                    className="form-control"
                    value={useCreditPlayerSearch}
                    onChange={(event) => {
                      setUseCreditPlayerSearch(event.target.value);
                      setUseCreditPlayerId("");
                    }}
                    placeholder="Search name, email, or ID"
                  />
                  <select
                    className="form-select mt-2"
                    value={useCreditPlayerId}
                    onChange={(event) => setUseCreditPlayerId(event.target.value)}
                  >
                    <option value="">Select player</option>
                    {useCreditPlayers.map((player) => {
                      const id = player.user_id;
                      return (
                        <option key={id} value={id}>
                          {player.full_name || player.name || "Unknown player"} ({player.email || "no email"}) #{id}
                        </option>
                      );
                    })}
                  </select>
                </div>
                <div className="col-12 col-md-4">
                  <label className="form-label">Compatible credit</label>
                  <select
                    className="form-select"
                    value={useCreditPurchaseId}
                    onChange={(event) => setUseCreditPurchaseId(event.target.value)}
                    disabled={!previewPurchases.length}
                  >
                    <option value="">
                      {previewPurchases.length ? "Auto select oldest credit" : "No compatible credits"}
                    </option>
                    {previewPurchases.map((purchase) => {
                      const paymentState = getPurchasePaymentState(purchase);
                      return (
                        <option key={purchase.purchase_id} value={purchase.purchase_id}>
                          {purchase.package_name} #{purchase.purchase_id} - {purchase.available} left - {paymentState.label}
                        </option>
                      );
                    })}
                  </select>
                </div>
                <div className="col-12">
                  {useCreditPreviewLoading ? (
                    <div className="credits-preview-box text-muted">Loading lesson and credit preview...</div>
                  ) : null}
                  {useCreditPreview?.lesson ? (
                    <div className="credits-preview-grid">
                      <div className="credits-preview-box">
                        <div className="credits-preview-label">Lesson</div>
                        <strong>
                          #{useCreditPreview.lesson.lesson_id} {useCreditPreview.lesson.title || useCreditPreview.lesson.lesson_type_name || ""}
                        </strong>
                        <div>
                          {useCreditPreview.lesson.lesson_type} credit required
                          {useCreditPreview.lesson.player_limit
                            ? `, ${useCreditPreview.lesson.open_spots} of ${useCreditPreview.lesson.player_limit} spots open`
                            : ""}
                        </div>
                        {useCreditPreview.lesson.is_full ? (
                          <div className="text-danger">Lesson full. Admin credit use blocked.</div>
                        ) : null}
                      </div>
                      <div className="credits-preview-box">
                        <div className="credits-preview-label">Selected credit</div>
                        {selectedPreviewPurchase ? (
                          <>
                            <strong>{selectedPreviewPurchase.package_name}</strong>
                            <div>
                              Purchase #{selectedPreviewPurchase.purchase_id}, {selectedPreviewPurchase.available} credits left
                            </div>
                            <div>
                              <span className={getPurchasePaymentState(selectedPreviewPurchase).className}>
                                {getPurchasePaymentState(selectedPreviewPurchase).label}
                              </span>
                            </div>
                          </>
                        ) : (
                          <div>No compatible available credit.</div>
                        )}
                      </div>
                    </div>
                  ) : null}
                </div>
                <div className="col-12">
                  <button
                    type="submit"
                    className="btn btn-primary w-100"
                    disabled={
                      useCreditLoading ||
                      useCreditPreviewLoading ||
                      useCreditPreview?.lesson?.is_full ||
                      !previewPurchases.length
                    }
                  >
                    {useCreditLoading ? "Using credit..." : "Use credit and confirm"}
                  </button>
                </div>
              </form>
            </div>

            <div className="col-12 col-lg-5">
              <div className="mb-3">
                <h5 className="mb-1">Rollback lesson credit</h5>
                <p className="text-muted mb-0">
                  Return the used credit and set the participant back to pending.
                </p>
              </div>
              {rollbackError ? (
                <div className="alert alert-danger py-2">{rollbackError}</div>
              ) : null}
              {rollbackMessage ? (
                <div className="alert alert-success py-2">{rollbackMessage}</div>
              ) : null}
              <form className="row g-2 align-items-end" onSubmit={handleRollbackCreditForLesson}>
                <div className="col-12 col-md-4">
                  <label className="form-label">Lesson ID</label>
                  <input
                    type="number"
                    className="form-control"
                    value={rollbackLessonId}
                    onChange={(event) => setRollbackLessonId(event.target.value)}
                    placeholder="2038"
                    min="1"
                  />
                </div>
                <div className="col-12 col-md-4">
                  <label className="form-label">Participant ID</label>
                  <input
                    type="number"
                    className="form-control"
                    value={rollbackParticipantId}
                    onChange={(event) => setRollbackParticipantId(event.target.value)}
                    placeholder="817"
                    min="1"
                  />
                </div>
                <div className="col-12 col-md-4">
                  <button
                    type="submit"
                    className="btn btn-outline-danger w-100"
                    disabled={rollbackLoading}
                  >
                    {rollbackLoading ? "Rolling back..." : "Rollback credit"}
                  </button>
                </div>
              </form>
            </div>

            <div className="col-12">
              <div className="credits-manual-adjustment">
                <div className="mb-3">
                  <h5 className="mb-1">Manual credit adjustment</h5>
                  <p className="text-muted mb-0">
                    Consume package credits for lessons taken outside the app.
                  </p>
                </div>
                {manualError ? (
                  <div className="alert alert-danger py-2">{manualError}</div>
                ) : null}
                {manualMessage ? (
                  <div className="alert alert-success py-2">{manualMessage}</div>
                ) : null}
                <form className="row g-2 align-items-end" onSubmit={handleManualConsumeCredit}>
                  <div className="col-12 col-lg-4">
                    <label className="form-label">Search purchases</label>
                    <input
                      type="search"
                      className="form-control"
                      value={manualPurchaseSearch}
                      onChange={(event) => {
                        setManualPurchaseSearch(event.target.value);
                        setManualError("");
                      }}
                      placeholder="Name, email, purchase ID"
                    />
                  </div>
                  <div className="col-12 col-lg-8">
                    <label className="form-label">Package purchase</label>
                    <select
                      className="form-select"
                      value={manualPurchaseId}
                      onChange={(event) => {
                        setManualPurchaseId(event.target.value);
                        setManualError("");
                      }}
                    >
                      <option value="">Select purchase</option>
                      {manualPurchaseOptions.map((purchase) => (
                        <option key={purchase.id} value={purchase.id}>
                          {formatManualPurchaseOption(purchase)}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-12 col-md-3 col-lg-2">
                    <label className="form-label">Amount</label>
                    <input
                      type="number"
                      className="form-control"
                      min="1"
                      max={manualAvailable || undefined}
                      value={manualAmount}
                      onChange={(event) => setManualAmount(event.target.value)}
                      placeholder="9"
                    />
                  </div>
                  <div className="col-12 col-md-9 col-lg-4">
                    <label className="form-label">Reason</label>
                    <textarea
                      className="form-control"
                      rows="2"
                      value={manualReason}
                      onChange={(event) => setManualReason(event.target.value)}
                      placeholder="Lessons taken outside the app"
                    />
                  </div>
                  {manualPurchase ? (
                    <div className="col-12">
                      <div className="credits-preview-box">
                        <div className="credits-preview-label">Selected purchase</div>
                        <strong>
                          Purchase #{manualPurchase.id}, {manualAvailable} credits available
                        </strong>
                        <div>
                          Purchased {formatPurchaseDate(manualPurchase)}
                        </div>
                        <div>
                          {manualPurchase.player_email || "-"} / {manualPurchase.coach_email || "-"}
                        </div>
                      </div>
                    </div>
                  ) : null}
                  <div className="col-12 col-md-4 col-lg-3">
                    <button
                      type="submit"
                      className="btn btn-outline-primary w-100"
                      disabled={manualSubmitDisabled}
                    >
                      {manualLoading ? "Adjusting..." : "Consume credits"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="card mb-3">
        <div className="card-body">
          <div className="row g-2 align-items-end">
            <div className="col-12 col-md-2">
              <label className="form-label">From</label>
              <input
                type="date"
                className="form-control"
                value={dateFrom}
                onChange={(event) => setDateFrom(event.target.value)}
              />
            </div>
            <div className="col-12 col-md-2">
              <label className="form-label">To</label>
              <input
                type="date"
                className="form-control"
                value={dateTo}
                onChange={(event) => setDateTo(event.target.value)}
              />
            </div>
            <div className="col-12 col-md-2">
              <label className="form-label">Coach</label>
              <input
                type="text"
                className="form-control"
                placeholder="Name, email, or ID"
                value={coachFilter}
                onChange={(event) => setCoachFilter(event.target.value)}
              />
            </div>
            <div className="col-12 col-md-2">
              <label className="form-label">Player</label>
              <input
                type="text"
                className="form-control"
                placeholder="Name, email, or ID"
                value={playerFilter}
                onChange={(event) => setPlayerFilter(event.target.value)}
              />
            </div>
            <div className="col-12 col-md-2">
              <label className="form-label">Package</label>
              <input
                type="text"
                className="form-control"
                placeholder="Name or ID"
                value={packageFilter}
                onChange={(event) => setPackageFilter(event.target.value)}
              />
            </div>
            <div className="col-12 col-md-2">
              <label className="form-label">Entry Type</label>
              <select
                className="form-select"
                value={entryTypeFilter}
                onChange={(event) => setEntryTypeFilter(event.target.value)}
              >
                <option value="">All</option>
                {entryTypes.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-12 col-md-4">
              <label className="form-label">Search</label>
              <input
                type="text"
                className="form-control"
                placeholder="Search ledger, names, emails, packages"
                onChange={(event) => debouncedSearch(event.target.value)}
              />
            </div>
            <div className="col-12 col-md-2">
              <label className="form-label">Rows</label>
              <select
                className="form-select"
                value={perPage}
                onChange={(event) => setPerPage(Number(event.target.value))}
              >
                {[10, 20, 50, 100].map((value) => (
                  <option key={value} value={value}>
                    {value}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      <ul className="nav nav-tabs">
        {[
          { id: "coach", label: "By Coach" },
          { id: "player", label: "By Player" },
          { id: "package", label: "By Package" },
          { id: "held", label: "Held Lessons" },
        ].map((tab) => (
          <li className="nav-item" key={tab.id}>
            <button
              type="button"
              className={`nav-link ${activeTab === tab.id ? "active" : ""}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          </li>
        ))}
      </ul>

      {confirmHeldError ? (
        <div className="alert alert-danger mt-3">{confirmHeldError}</div>
      ) : null}
      {confirmHeldMessage ? (
        <div className="alert alert-success mt-3">{confirmHeldMessage}</div>
      ) : null}

      <div className="tab-content">
        <div
          className={`tab-pane fade ${
            activeTab === "coach" ? "show active" : ""
          }`}
        >
          <div className="table-responsive credits-table-wrapper">
            <table className="table table-striped table-sm align-middle">
              <thead>
                <tr>
                  <th>Coach</th>
                  <th>Email</th>
                  <th>Available</th>
                  <th>Held</th>
                  <th>Total</th>
                  <th>Earned</th>
                  <th>Consumed</th>
                  <th>Expired</th>
                  <th>Refunded</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan="9" className="text-center">
                      Loading credits...
                    </td>
                  </tr>
                ) : coachRows.length ? (
                  coachRows.map((row) => (
                    <tr key={row.key}>
                      <td>{row.name}</td>
                      <td>{row.email}</td>
                      <td>{formatNumber(row.available)}</td>
                      <td>{formatNumber(row.held)}</td>
                      <td>{formatNumber(row.total)}</td>
                      <td>{formatNumber(row.earned)}</td>
                      <td>{formatNumber(row.consumed)}</td>
                      <td>{formatNumber(row.expired)}</td>
                      <td>{formatNumber(row.refunded)}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="9" className="text-center">
                      No coach credits found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {byCoach.length > perPage ? (
            <Paginations
              perPage={perPage}
              total={byCoach.length}
              paginate={setPageCoach}
              currentPage={pageCoach}
            />
          ) : null}
        </div>

        <div
          className={`tab-pane fade ${
            activeTab === "player" ? "show active" : ""
          }`}
        >
          <div className="table-responsive credits-table-wrapper">
            <table className="table table-striped table-sm align-middle">
              <thead>
                <tr>
                  <th>Player</th>
                  <th>Email</th>
                  <th>Available</th>
                  <th>Held</th>
                  <th>Total</th>
                  <th>Earned</th>
                  <th>Consumed</th>
                  <th>Expired</th>
                  <th>Refunded</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan="9" className="text-center">
                      Loading credits...
                    </td>
                  </tr>
                ) : playerRows.length ? (
                  playerRows.map((row) => (
                    <tr key={row.key}>
                      <td>{row.name}</td>
                      <td>{row.email}</td>
                      <td>{formatNumber(row.available)}</td>
                      <td>{formatNumber(row.held)}</td>
                      <td>{formatNumber(row.total)}</td>
                      <td>{formatNumber(row.earned)}</td>
                      <td>{formatNumber(row.consumed)}</td>
                      <td>{formatNumber(row.expired)}</td>
                      <td>{formatNumber(row.refunded)}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="9" className="text-center">
                      No player credits found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {byPlayer.length > perPage ? (
            <Paginations
              perPage={perPage}
              total={byPlayer.length}
              paginate={setPagePlayer}
              currentPage={pagePlayer}
            />
          ) : null}
        </div>

        <div
          className={`tab-pane fade ${
            activeTab === "package" ? "show active" : ""
          }`}
        >
          <div className="table-responsive credits-table-wrapper">
            <table className="table table-striped table-sm align-middle">
              <thead>
                <tr>
                  <th>Package</th>
                  <th>Coach</th>
                  <th>Coach Email</th>
                  <th>Available</th>
                  <th>Held</th>
                  <th>Total</th>
                  <th>Earned</th>
                  <th>Consumed</th>
                  <th>Expired</th>
                  <th>Refunded</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan="10" className="text-center">
                      Loading credits...
                    </td>
                  </tr>
                ) : packageRows.length ? (
                  packageRows.map((row) => (
                    <tr key={row.key}>
                      <td>{row.name}</td>
                      <td>{row.coach_name}</td>
                      <td>{row.email}</td>
                      <td>{formatNumber(row.available)}</td>
                      <td>{formatNumber(row.held)}</td>
                      <td>{formatNumber(row.total)}</td>
                      <td>{formatNumber(row.earned)}</td>
                      <td>{formatNumber(row.consumed)}</td>
                      <td>{formatNumber(row.expired)}</td>
                      <td>{formatNumber(row.refunded)}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="10" className="text-center">
                      No package credits found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {byPackage.length > perPage ? (
            <Paginations
              perPage={perPage}
              total={byPackage.length}
              paginate={setPagePackage}
              currentPage={pagePackage}
            />
          ) : null}
        </div>

        <div
          className={`tab-pane fade ${
            activeTab === "held" ? "show active" : ""
          }`}
        >
          <div className="table-responsive credits-table-wrapper">
            <table className="table table-striped table-sm align-middle">
              <thead>
                <tr>
                  <th>Player</th>
                  <th>Coach</th>
                  <th>Lesson</th>
                  <th>Participant</th>
                  <th>Purchase</th>
                  <th>Payment</th>
                  <th>Held</th>
                  <th>Created</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {loadingLedger ? (
                  <tr>
                    <td colSpan="9" className="text-center">
                      Loading held credits...
                    </td>
                  </tr>
                ) : heldRows.length ? (
                  heldRows.map((row) => {
                    const purchase = purchaseMap.get(String(row.purchase_id));
                    const paymentState = getPurchasePaymentState(purchase);
                    const rowWithPaymentState = { ...row, payment_state: paymentState };
                    return (
                      <tr key={row.key}>
                        <td>
                          <div>{row.player_name}</div>
                          <small className="text-muted">{row.player_email}</small>
                        </td>
                        <td>
                          <div>{row.coach_name}</div>
                          <small className="text-muted">{row.coach_email}</small>
                        </td>
                        <td>{row.lesson_id}</td>
                        <td>{row.participant_id || "-"}</td>
                        <td>{row.purchase_id || "-"}</td>
                        <td>
                          <span className={paymentState.className}>
                            {paymentState.label}
                          </span>
                        </td>
                        <td>{formatNumber(row.active_held)}</td>
                        <td>
                          {row.created_at
                            ? new Date(row.created_at).toLocaleString()
                            : "-"}
                        </td>
                        <td>
                          <button
                            type="button"
                            className="btn btn-sm btn-primary"
                            disabled={
                              confirmingHeldKey === row.key ||
                              !row.lesson_id ||
                              !row.participant_id
                            }
                            onClick={() => handleConfirmHeldCredit(rowWithPaymentState)}
                          >
                            {confirmingHeldKey === row.key
                              ? "Confirming..."
                              : paymentState.isReserved
                                ? "Charge and confirm"
                                : "Retry confirmation"}
                          </button>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="9" className="text-center">
                      No active held lesson credits found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {heldLessonRows.length > perPage ? (
            <Paginations
              perPage={perPage}
              total={heldLessonRows.length}
              paginate={setPageHeld}
              currentPage={pageHeld}
            />
          ) : null}
        </div>
      </div>

      {!loadingLedger && filteredLedger.length === 0 ? (
        <div className="alert alert-light mt-3">
          No ledger entries match the current filters.
        </div>
      ) : null}
    </main>
  );
}

export default CreditsDashboard;
